
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  subcategory?: string;
  description: string;
  imageUrl: string;
  createdAt: Date;
  // Additional fields
  buyPrice?: number; // Internal admin-only field
  sellPrice?: number; // This will be shown to customers as price
  images?: string[];
  colors?: { id: string; name: string; hex: string }[];
  sizes?: string[]; // Optional array of size variants
  discount?: number; // Percentage discount
  couponCode?: string; // Coupon code if applicable
}

const fetchProducts = async (): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const productsSnapshot = await getDocs(productsCollection);
    
    return productsSnapshot.docs.map(doc => {
      const data = doc.data();
      // Convert Firestore timestamp to Date
      const createdAt = data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate() 
        : new Date();
      
      return {
        id: doc.id,
        name: data.name || '',
        price: Number(data.sellPrice || data.price) || 0, // Use sellPrice as the public price
        category: data.category || '',
        subcategory: data.subcategory || '',
        description: data.description || '',
        imageUrl: data.imageUrl || '',
        createdAt,
        // Additional fields
        buyPrice: data.buyPrice ? Number(data.buyPrice) : undefined, // Admin-only
        sellPrice: data.sellPrice ? Number(data.sellPrice) : Number(data.price) || 0,
        images: data.images || [],
        colors: data.colors || [],
        sizes: data.sizes || [],
        discount: data.discount ? Number(data.discount) : undefined,
        couponCode: data.couponCode || '',
      };
    });
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

const fetchProduct = async (id: string): Promise<Product | null> => {
  try {
    const productRef = doc(db, 'products', id);
    const productSnapshot = await getDoc(productRef);
    
    if (!productSnapshot.exists()) {
      return null;
    }
    
    const data = productSnapshot.data();
    
    // Convert Firestore timestamp to Date
    const createdAt = data.createdAt instanceof Timestamp 
      ? data.createdAt.toDate() 
      : new Date();
    
    return {
      id: productSnapshot.id,
      name: data.name || '',
      price: Number(data.sellPrice || data.price) || 0,
      category: data.category || '',
      subcategory: data.subcategory || '',
      description: data.description || '',
      imageUrl: data.imageUrl || '',
      createdAt,
      buyPrice: data.buyPrice ? Number(data.buyPrice) : undefined,
      sellPrice: data.sellPrice ? Number(data.sellPrice) : Number(data.price) || 0,
      images: data.images || [],
      colors: data.colors || [],
      sizes: data.sizes || [],
      discount: data.discount ? Number(data.discount) : undefined,
      couponCode: data.couponCode || '',
    };
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
};

const updateProduct = async (id: string, data: Partial<Omit<Product, 'id' | 'createdAt'>>) => {
  try {
    const productRef = doc(db, 'products', id);
    await updateDoc(productRef, data);
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

const deleteProduct = async (id: string) => {
  try {
    const productRef = doc(db, 'products', id);
    await deleteDoc(productRef);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

const addProduct = async (product: Omit<Product, 'id' | 'createdAt'>) => {
  try {
    const productsCollection = collection(db, 'products');
    await addDoc(productsCollection, {
      ...product,
      createdAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

export const useProducts = () => {
  const {
    data: products = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts
  });

  return {
    products,
    isLoading,
    error,
    refetch,
    fetchProduct,
    updateProduct,
    deleteProduct,
    addProduct
  };
};
